import React, { useState, useEffect } from 'react';
import { Tooltip, OverlayTrigger, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getOrderById, getOrderItemsByOrderId, logError, handleSaveEditOrder, updateOrder } from '../../api';
import { handleDownloadPhotos } from '../Global/DownloadUtils';  // Importamos la función de fotos
import PhotoUploader from '../Global/PhotoUploader';
import EditItemsInOrder from '../Global/EditItemsInOrder';
import EditClientInOrder from '../Global/EditClientInOrder';
import DeliveryDateEditor from '../Global/DeliveryDateEditor';  
import CarrierAssigner from '../Global/CarrierAssigner'; 
import CommentsInfoRow from '../Global/CommentsInfoRow';
import ItemsInfoRow from '../Global/ItemsInfoRow';
import ClientInfoRow from './ClientInfoRow';
import InfoToggle from '../Global/InfoToggle';
import ActionButtons from './ActionButtons';
import { useNavigate } from 'react-router-dom';
import { isComercialAndNotOwnerAdminGestion, isJefeComerciales, isPropietarioOrAdminOrGestion, getSalesRepId, isLeads, isComercial } from '../../services/AuthService'; 
import IncidentForm from '../IncidentForm';
import TimeSlotAssigner from '../Global/TimeSlotAssigner';
import ClientOriginAssigner from '../Global/ClientOriginAssigner';
import PaymentMethodAssigner from '../Global/PaymentMethodAssigner';

const OrderDetail = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [detailedItems, setDetailedItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [mileage, setMileage] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedOrderItems, setEditedOrderItems] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isClientDetailsVisible, setIsClientDetailsVisible] = useState(false);
  const [photos, setPhotos] = useState([]); // Estado para almacenar las fotos
  const [showPhotosModal, setShowPhotosModal] = useState(false); // Estado para controlar la modal de fotos
  const navigate = useNavigate();
  const [orderItemsUpdated, setOrderItemsUpdated] = useState(false);
  const [isIncidentVisible, setIsIncidentVisible] = useState(false);
  const [hasEmptySpecialDescription, setHasEmptySpecialDescription] = useState(false);
  const [infoPhotos, setInfoPhotos] = useState({ photo1: null, photo2: null });
  const [isEditingPhotos, setIsEditingPhotos] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editedPrepaidAmount, setEditedPrepaidAmount] = useState(null);

  const toggleIncident = () => {
    setIsIncidentVisible(prev => !prev);
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // Validar descripciones de productos especiales
  useEffect(() => {
    if (!isEditable) return;
    if (!editedOrderItems.specialProducts) {
      setHasEmptySpecialDescription(false);
      return;
    }
    const anyEmpty = (editedOrderItems.specialProducts || []).some(
      product => !product.description || product.description.trim() === ''
    );
    setHasEmptySpecialDescription(anyEmpty);
  }, [editedOrderItems, isEditable]);

  const fetchOrderDetails = async () => {
    try {
      const fetchedOrder = await getOrderById(orderId);
      setOrder(fetchedOrder);
      setMileage(fetchedOrder.mileage);
      setDetailedItems(fetchedOrder.items || []);
      setOriginalItems(fetchedOrder.items || []);
      
      // No cargar fotos automáticamente
      
      // Si hay productos especiales, agregarlos a los detalles
      if (fetchedOrder.specialProducts?.length > 0) {
        setOrder(prev => ({
          ...prev,
          specialProducts: fetchedOrder.specialProducts
        }));
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const handleClientToggle = () => {
    setIsClientDetailsVisible((prev) => !prev);
  };

  const handleOrderMileageChange = (newMileage) => {
    setMileage(newMileage);
  };

  const handleCancelEdit = () => {
    setDetailedItems(originalItems);
    setIsEditable(false);
  };

  const toggleEditClient = () => setIsEditingClient(prev => !prev);

  const toggleEdit = async () => {
    if (isEditable) {
      handleCancelEdit();
      setIsEditable(false);
    } else {
      if (!detailedItems[orderId]) {
        try {
          const itemsWithDetails = await getOrderItemsByOrderId(orderId);
          setDetailedItems(itemsWithDetails);
          setEditedPrepaidAmount(order.alreadyPaid > 0 ? order.alreadyPaid : 0);
          setIsEditable(true);
        } catch (error) {
          console.error('Error fetching item details:', error);
        }
      }
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);

      await handleSaveEditOrder(
        orderId,
        editedOrderItems,
        mileage,
        setSuccessMessage,
      );

      setIsSaving(false);
      setIsEditable(false);
      fetchOrderDetails();
      setOrderItemsUpdated(prev => !prev);
    } catch (error) {
      setIsSaving(false);
      console.error('Error saving order changes:', error);
      logError(error);
      alert('Error al guardar los cambios. Por favor, intente de nuevo.');
    }
  };

  const handlePhotos = async () => {
    if (photos.length === 0) {
      const fetchedPhotos = await handleDownloadPhotos(orderId);
      setPhotos(fetchedPhotos);
    }
    setShowPhotosModal(true); // Muestra la modal con las fotos
  };

  const handleClosePhotosModal = () => {
    setShowPhotosModal(false); // Cierra la modal
  };

  const loadInfoPhotos = async () => {
    if (order.hasImage && !infoPhotos.photo1 && !infoPhotos.photo2) {
      try {
        const fetchedPhotos = await handleDownloadPhotos(orderId);
        setInfoPhotos({
          photo1: fetchedPhotos[0] || null,
          photo2: fetchedPhotos[1] || null
        });
      } catch (error) {
        console.error('Error loading info photos:', error);
      }
    }
  };

  const handleSaveInfoPhotos = async (newPhotos) => {
    try {
      const updateData = {
        id: orderId,
        ...(newPhotos.photo1 !== null && { InfoPhoto1Base64: newPhotos.photo1 }),
        ...(newPhotos.photo2 !== null && { InfoPhoto2Base64: newPhotos.photo2 })
      };
      
      await updateOrder(orderId, updateData);
      setInfoPhotos(newPhotos);
      setIsEditingPhotos(false);
      setSuccessMessage('Fotos informativas actualizadas correctamente.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving info photos:', error);
      logError(error);
      alert('Error al guardar las fotos. Por favor, intente de nuevo.');
    }
  };

  if (!order) {
    return <div>Cargando...</div>;
  }

  const isReadonly = !isPropietarioOrAdminOrGestion() && !isJefeComerciales() && !isComercial();

  return (
    <div className="order-detail" data-testid="order-detail">
      <h2 data-testid="order-detail-title">Detalles del Pedido</h2>

      <h3 data-testid="actions-title">Acciones</h3>
      <ActionButtons
        order={order}
        navigate={navigate}
        toggleEditRow={toggleEdit}
        toggleEditClientRow={toggleEditClient}
        handlePhotos={handlePhotos}
        useTextButtons={true}
        readOnly={isReadonly}
        toggleIncidentRow={toggleIncident}
      />

      <p/>

      {isIncidentVisible && (
        <div className="mt-4">
          <h4>Registrar Incidencia</h4>
          <IncidentForm orderId={order.id} initialMileage={order.mileage} />
        </div>
      )}

      {isEditingClient && (
        <EditClientInOrder
          order={order}
          onSaved={() => {
            setIsEditingClient(false);
            fetchOrderDetails();
            setSuccessMessage('Cliente actualizado correctamente.');
            setTimeout(() => setSuccessMessage(''), 3000);
          }}
          onCancel={() => setIsEditingClient(false)}
        />
      )}

      {isEditable && (
        <div>
          <span className="order-section">
            <EditItemsInOrder
              initialItems={detailedItems}
              initialSpecialProducts={order.specialProducts || []}
              onItemsChange={(updatedItems, updatedSpecialProducts) => {
                setEditedOrderItems({
                  items: updatedItems,
                  specialProducts: updatedSpecialProducts
                });
              }}
              initialMileage={order.mileage}
              initialPrepaidAmount={order.alreadyPaid > 0 ? order.alreadyPaid : null}
              onOrderMileageChange={handleOrderMileageChange}
              onPrepaidAmountChange={(val) => setEditedPrepaidAmount(val)}
              onSave={async (items, specialProducts, mileage) => {
                await handleSaveEditOrder(orderId, { items, specialProducts }, mileage, setSuccessMessage, editedPrepaidAmount);
                setIsEditable(false);
                fetchOrderDetails();
                setOrderItemsUpdated(prev => !prev);
              }}
              onCancel={handleCancelEdit}
              saveLabel="Guardar Cambios"
            />
          </span>
        </div>
      )}

      <div>
        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        <p className="order-section" data-testid="order-id-section">
          <strong>Id: </strong>
          {order.parentId !== null ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Este pedido proviene de una incidencia</Tooltip>}
            >
              <span style={{ color: 'red', fontWeight: 'bold' }} data-testid="order-external-id-incident">
                {order.externalId || 'N/A'}
              </span>
            </OverlayTrigger>
          ) : (
            <span data-testid="order-external-id">{order.externalId || 'N/A'}</span>
          )}
        </p>

        {/* Mostrar detalles del cliente con toggle */}
        <div data-testid="client-section">
        <strong>Cliente: </strong>
        <InfoToggle
          label={`${order.client?.name ?? ''} ${order.client?.lastName1 ?? ''} ${order.client?.lastName2 ?? ''}`}
          openState={isClientDetailsVisible}
          handleToggle={handleClientToggle}
        />
        </div>
        
        {isClientDetailsVisible && (
          <div data-testid="client-details-expanded">
            <ClientInfoRow order={order} />
          </div>
        )}

        <p/>

        <p className="order-section" data-testid="sales-rep-section">
          <strong>Comercial:</strong> {order.salesRep ? `${order.salesRep.name} ${order.salesRep.lastName1}` : 'Sin Asignar'}
        </p>

        <p className="order-section" data-testid="order-date-section">
          <strong>Fecha de Pedido:</strong> {new Date(order.orderDate).toLocaleString('es-ES')}
        </p>

        <div className="order-section" data-testid="important-order-section">
          <strong>Pedido Importante: </strong>
          {!isReadonly ? (
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="isImportantCheckbox"
                checked={order.isImportant || false}
                onChange={async (e) => {
                  const newImportantState = e.target.checked;
                  try {
                    await updateOrder(orderId, { id: orderId, isImportant: newImportantState });
                    setOrder(prev => ({ ...prev, isImportant: newImportantState }));
                    setSuccessMessage(`Pedido ${newImportantState ? 'marcado como importante' : 'desmarcado como importante'}.`);
                    setTimeout(() => setSuccessMessage(''), 3000);
                  } catch (error) {
                    console.error('Error updating order importance:', error);
                    logError(error);
                    alert('Error al actualizar la importancia del pedido.');
                    // Revertir el checkbox en caso de error
                    e.target.checked = !newImportantState;
                  }
                }}
                data-testid="important-order-checkbox"
              />
              <label className="form-check-label" htmlFor="isImportantCheckbox">
                {order.isImportant ? '⭐ Sí' : 'No'}
              </label>
            </div>
          ) : (
            <span data-testid="important-order-readonly">
              {order.isImportant ? '⭐ Sí' : 'No'}
            </span>
          )}
        </div>

        <p className="order-section">
          <strong>Estado:  </strong>
          {(order.status || 'N/A')}
          <br/>
          {order.hasIncident && (
            <span>
              <strong>Razón: </strong>{order.incidentType?.name || 'N/A'}
              <br/>
              <strong>Acción: </strong>{order.incidentAction?.name || 'N/A'}
            </span>
          )}
          {order.deliveredTime !== null && (
            <span>
              <strong>Fecha de Firma: </strong>{order.signedTime ? new Date(order.signedTime).toLocaleString('es-ES') : 'N/A'}
              <br/>
              <strong>Entregado: </strong>{new Date(order.deliveredTime).toLocaleString('es-ES')}
            </span>
          )}
          
        </p>

        <span className="order-section" data-testid="delivery-date-section">
          <strong>Fecha de Entrega: </strong>
          <DeliveryDateEditor order={order} />
        </span>

        <p/>

        <span className="order-section" data-testid="time-slot-section">
          <strong>Horario de Entrega: </strong>
          <TimeSlotAssigner order={order} />
        </span>

        <p/>

        <span className="order-section" data-testid="client-origin-section">
          <strong>Origen del Cliente: </strong>
          <ClientOriginAssigner order={order} />
        </span>

        <p/>

        <span className="order-section" data-testid="payment-method-section">
          <strong>Método de Pago: </strong>
          <PaymentMethodAssigner order={order} />
        </span>

        <p/>

        <span className="order-section" data-testid="amounts-section">
          {order.alreadyPaid > 0 ? (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.5' }} data-testid="detailed-amounts">
              <div data-testid="total-amount">
                <strong>Total: </strong>
                {order.totalAmount !== undefined && order.totalAmount !== null
                  ? order.totalAmount.toFixed(2)
                  : 'N/A'}€
              </div>
              <div data-testid="paid-amount">
                <strong>Pagado: </strong>
                {order.alreadyPaid ? order.alreadyPaid.toFixed(2) : 'N/A'}€
              </div>
              <div data-testid="pending-amount">
                <strong>Pendiente: </strong>
                {order.pendingAmount ? order.pendingAmount.toFixed(2) : 'N/A'}€
              </div>
            </div>
          ) : (
            <div data-testid="simple-total"><strong>Total: </strong>{order.totalAmount !== undefined && order.totalAmount !== null ? order.totalAmount.toFixed(2) : 'N/A'}€
            </div>
          )}
        </span>

        <p/>

        {!isComercialAndNotOwnerAdminGestion() && (
          <span className="order-section" data-testid="carrier-section">
            <strong>Transportista: </strong>
            <CarrierAssigner order={order} />
          </span>
        )}

        <p/>

        <CommentsInfoRow order={order} readOnly={isReadonly} />
        <ItemsInfoRow order={order} refresh={orderItemsUpdated} />
        
        {/* Fotos informativas */}
        <div className="order-section">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Fotos informativas:</strong>
            {!isReadonly && (
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  if (!isEditingPhotos) {
                    loadInfoPhotos();
                  }
                  setIsEditingPhotos(!isEditingPhotos);
                }}
              >
                {isEditingPhotos ? 'Cancelar' : 'Editar fotos'}
              </button>
            )}
          </div>
          
          {isEditingPhotos ? (
            <PhotoUploader 
              initialPhotos={infoPhotos}
              onPhotosChange={handleSaveInfoPhotos}
              title=""
            />
          ) : (
            <div className="row">
              {infoPhotos.photo1 && (
                <div className="col-md-6">
                  <img 
                    src={`data:image/jpeg;base64,${infoPhotos.photo1}`}
                    alt="Foto informativa 1"
                    style={{ width: '100%', maxWidth: '200px', height: 'auto' }}
                    className="mb-2"
                  />
                </div>
              )}
              {infoPhotos.photo2 && (
                <div className="col-md-6">
                  <img 
                    src={`data:image/jpeg;base64,${infoPhotos.photo2}`}
                    alt="Foto informativa 2"
                    style={{ width: '100%', maxWidth: '200px', height: 'auto' }}
                    className="mb-2"
                  />
                </div>
              )}
              {!infoPhotos.photo1 && !infoPhotos.photo2 && (
                <p className="text-muted">{order.hasImage ? 'Haz clic en "Editar fotos" para ver o modificar las fotos informativas.' : 'No hay fotos informativas.'}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de fotos */}
      <Modal show={showPhotosModal} onHide={handleClosePhotosModal}>
        <Modal.Header closeButton>
          <Modal.Title>Fotos del Pedido</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Mostrar las fotos si existen */}
          {photos.length > 0 ? (
            photos.map((photo, index) => (
              <img key={index} src={`data:image/jpeg;base64,${photo}`} alt={`Foto ${index + 1}`} style={{ width: '100%', marginBottom: '10px' }} />
            ))
          ) : (
            <p>No hay fotos disponibles.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleClosePhotosModal}>Cerrar</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderDetail;
